import { AnimatePresence } from 'motion/react';
import { useState } from 'react';

import { ApiError, api } from '../api/client';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { Modal } from './Modal';

/**
 * One-time opt-in prompt, shown right after an org's FIRST-EVER top-up
 * completes — see TopUp.tsx for how "first" is determined (payment history
 * was empty before this session's top-up). Not shown again after either
 * button is pressed; the persistent toggle on this same page
 * (AutoFallbackToggle) is the only way to change the setting afterward,
 * including for an org that never sees this prompt at all because it
 * topped up before this feature shipped.
 *
 * `dismissible={false}`: same reasoning as OrgChooserModal — this is a
 * deliberate money decision, not something to lose to a stray backdrop
 * click. Declining is a real, explicit answer (button), not "closing".
 */
export function FirstTopUpPrompt({ onDone }: { onDone: () => void }) {
  const { dict } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function answer(enabled: boolean) {
    setBusy(true);
    setError(null);
    try {
      await api.setAutoFallback(enabled);
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.quota.saveError);
      setBusy(false);
    }
  }

  return (
    <AnimatePresence>
      <Modal title={dict.firstTopUpPrompt.title} dismissible={false}>
        <p className="note">{dict.firstTopUpPrompt.body}</p>
        {error && <div className="err">{error}</div>}
        <div className="modal-actions">
          <button type="button" disabled={busy} onClick={() => void answer(true)}>
            {dict.firstTopUpPrompt.accept}
          </button>
          <button type="button" className="ghost" disabled={busy} onClick={() => void answer(false)}>
            {dict.firstTopUpPrompt.decline}
          </button>
        </div>
      </Modal>
    </AnimatePresence>
  );
}
